import * as React from 'react';
import AssignmentLateOutlined from '@material-ui/icons/AssignmentLateOutlined';
import moment from 'moment';
import { CountryIso2 } from '../../../../helpers/enums';
import { fetchInsurancesV2 } from 'slices/insurancesV2';
import { fetchOrganizationInsuranceSectionsV2 } from 'slices/organizationInsuranceSectionsV2';
import { IInsuranceV2 } from 'interfaces/insurance';
import { localizeDate } from '@pec/aion-ui-i18next/helpers/localize';
import { PendingOutlinedIcon } from '../../PendingOutlinedIcon';
import { ToDoChild, ToDoDescriptor } from '../types';
import { useAppDispatch, useTypedSelector } from '../../../../combineReducers';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type RouteParams = {
  organizationId: string;
};

type InsuranceWithSection = IInsuranceV2 & { questionSectionId?: string };

export const useInsuranceToDo = (): ToDoDescriptor[] => {
  const dispatch = useAppDispatch();
  const { organizationId } = useParams<RouteParams>();
  const { t } = useTranslation('subscriber');
  const { insurancesV2: insurances, isFetching, error } = useTypedSelector(state => state.insurancesV2);
  const { organizationInsuranceSectionsV2 } = useTypedSelector(state => state.organizationInsuranceSectionsV2);
  const {
    mainSubscription: { countryISO2 }
  } = useTypedSelector(state => state.mainSubscription);

  React.useEffect(() => {
    const shouldFetch = ![CountryIso2.GB, CountryIso2.AU].includes(countryISO2 as CountryIso2);

    if (organizationId && shouldFetch) {
      dispatch(fetchOrganizationInsuranceSectionsV2(organizationId));
      dispatch(fetchInsurancesV2(organizationId));
    }
  }, [organizationId, dispatch, countryISO2]);

  const hasError = !!error;

  const insuranceWithSections: InsuranceWithSection[] = insurances.map(ins => {
    const section = organizationInsuranceSectionsV2.find(section => section.organizationId === ins.organizationId);
    return { ...ins, questionSectionId: section?.sectionId };
  });

  const reviewedInsurances = insuranceWithSections.filter(({ isPendingReview }) => !isPendingReview);

  const expired = reviewedInsurances.filter(({ earliestPolicyExpirationDateUtc }) =>
    moment(earliestPolicyExpirationDateUtc).isBefore()
  );
  const expiring = reviewedInsurances.filter(({ earliestPolicyExpirationDateUtc }) =>
    moment(earliestPolicyExpirationDateUtc).isBetween(moment(), moment().add(30, 'days'))
  );

  const toChildren = (list: InsuranceWithSection[], isExpired: boolean): ToDoChild[] =>
    list.map((insurance, index) => {
      const expirationDate =
        insurance.earliestPolicyExpirationDateUtc &&
        localizeDate({ value: insurance.earliestPolicyExpirationDateUtc, timeZoneId: 'UTC' }, t);
      return {
        id: `${insurance.organizationName}-${index}`,
        href: `/organizations/${organizationId}/questionnaire-v2/${insurance.questionSectionId}`,
        label: t(
          isExpired ? 'newDashboard.toDoWidget.expiredOn' : 'newDashboard.toDoWidget.expiresOn',
          isExpired ? '{{name}} expired on {{date}}' : '{{name}} expires on {{date}}',
          {
            name: insurance.organizationName,
            date: expirationDate
          }
        )
      };
    });

  return [
    {
      id: 'insurance.expired',
      variant: 'group',
      label: t('newDashboard.toDoWidget.updateExpiredInsurance', 'Update expired insurance'),
      icon: <AssignmentLateOutlined />,
      count: expired.length,
      children: toChildren(expired, true),
      isVisible: expired.length > 0 && !hasError,
      isFetching
    },
    {
      id: 'insurance.expiring',
      variant: 'group',
      label: t('newDashboard.toDoWidget.updateExpiringInsurance', 'Update insurance - expiring soon'),
      icon: <PendingOutlinedIcon />,
      count: expiring.length,
      children: toChildren(expiring, false),
      isVisible: expiring.length > 0 && !hasError,
      isFetching
    }
  ];
};
